import React from 'react';
import {
    Drawer,
    Loader,
    Timeline,
    Text,
    ThemeIcon,
    Box,
    Flex,
} from '@mantine/core';
import { IconClock, IconUser, IconDeviceDesktopCog } from '@tabler/icons-react';
import { useQuery } from '@apollo/client';
import dayjs from 'dayjs';
import {
    getSurveyActivityColor,
    getSurveyActivityDescription,
    getSurveyActivityIcon,
    getSurveyActivityLabel,
} from '../../utility/surveyActivityUtils';
import { GET_SURVEY_ACTIVITIES } from '../../apollo/queries/surveyActivity';
import { SurveyActivityDrawerHeader } from './SurveyActivityDrawerHeader';
import { SurveyActivityDrawerFooter } from './SurveyActivityDrawerFooter';

interface SurveyActivity {
    id: string;
    type: string;
    description: string;
    isSystemGenerated?: boolean;
    metadata?: {
        sku?: string;
        skus?: string;
        competitorName?: string;
        count?: number;
    } | null;
    createdAt: string;
    user?: { firstName: string; lastName: string } | null;
}

interface SurveyActivityDrawerProps {
    surveyId: string;
    opened: boolean;
    onClose: () => void;
    zoneName: string;
}

export function SurveyActivityDrawer({
    surveyId,
    opened,
    onClose,
    zoneName,
}: SurveyActivityDrawerProps) {
    const { data, loading, error } = useQuery(GET_SURVEY_ACTIVITIES, {
        variables: { surveyId },
        skip: !opened,
        fetchPolicy: 'network-only',
    });

    function renderDrawerContent() {
        if (loading) {
            return (
                <Box className="flex justify-center items-center py-10">
                    <Loader />
                </Box>
            );
        }
        if (error) {
            return (
                <Text c="red" ta="center">
                    Error loading history
                </Text>
            );
        }
        if (!data?.getSurveyActivities?.length) {
            return (
                <Text c="dimmed" ta="center">
                    No activity recorded yet.
                </Text>
            );
        }
        return (
            <Timeline
                bulletSize={34}
                lineWidth={2}
                classNames={{
                    itemBody: 'pt-1 pl-4',
                    itemBullet: '!bg-transparent !border-0 !shadow-none ring-0',
                }}
            >
                {data?.getSurveyActivities?.map((activity: SurveyActivity) => (
                    <Timeline.Item
                        key={activity.id}
                        bullet={
                            <Box className="bg-white rounded-full">
                                <ThemeIcon
                                    size={34}
                                    radius="xl"
                                    variant="light"
                                    color={getSurveyActivityColor(
                                        activity.type,
                                    )}
                                >
                                    {getSurveyActivityIcon(activity.type)}
                                </ThemeIcon>
                            </Box>
                        }
                    >
                        <Text
                            size="md"
                            fw={600}
                            className="text-gray-800 leading-tight mb-1"
                        >
                            {getSurveyActivityLabel(activity.type)}
                        </Text>

                        <Text
                            size="sm"
                            c="dimmed"
                            className="mb-2 leading-snug"
                        >
                            {getSurveyActivityDescription(activity)}
                        </Text>

                        <Flex className="flex-row items-center gap-4 text-gray-400 text-xs mt-2">
                            <Flex className="flex-row items-center gap-1.5">
                                {activity.isSystemGenerated ? (
                                    <>
                                        <IconDeviceDesktopCog
                                            size={14}
                                            stroke={1.5}
                                        />
                                        <Text
                                            size="xs"
                                            fw={500}
                                            component="span"
                                            className="text-gray-400"
                                        >
                                            System Generated
                                        </Text>
                                    </>
                                ) : (
                                    <>
                                        <IconUser size={14} stroke={1.5} />
                                        <Text
                                            size="xs"
                                            fw={500}
                                            component="span"
                                            className="text-gray-400"
                                        >
                                            {activity.user
                                                ? `${activity.user.firstName} ${activity.user.lastName}`
                                                : 'System'}
                                        </Text>
                                    </>
                                )}
                            </Flex>
                            <Flex className="flex-row items-center gap-1.5">
                                <IconClock size={14} stroke={1.5} />
                                <Text
                                    size="xs"
                                    component="span"
                                    className="text-gray-400"
                                >
                                    {dayjs(activity.createdAt).format(
                                        'YYYY-MM-DD HH:mm',
                                    )}
                                </Text>
                            </Flex>
                        </Flex>
                    </Timeline.Item>
                ))}
            </Timeline>
        );
    }

    const activityCount = data?.getSurveyActivities?.length ?? 0;
    const hasActivities = activityCount > 0;

    return (
        <Drawer
            opened={opened}
            onClose={onClose}
            title={<SurveyActivityDrawerHeader zoneName={zoneName} />}
            position="right"
            size="lg"
            padding={0}
            classNames={{
                header: 'items-start p-6 pb-4 border-b border-gray-200 mb-0 mr-0 shrink-0 bg-survey-drawer-header-bg',
                body: 'p-0 flex flex-col flex-1 min-h-0 overflow-hidden',
                close: 'mt-1',
                content: 'flex flex-col max-h-full',
            }}
        >
            <Flex className="flex-col flex-1 min-h-0">
                <Box className="flex-1 min-h-0 overflow-y-auto p-6">
                    {renderDrawerContent()}
                </Box>
                <SurveyActivityDrawerFooter
                    surveyId={surveyId}
                    activityCount={activityCount}
                    loading={loading}
                    hasError={!!error}
                    hasActivities={hasActivities}
                />
            </Flex>
        </Drawer>
    );
}
